import { MapPin, Phone, ExternalLink, Store, Map, MessageCircle, Instagram, Facebook, Globe, BadgeCheck } from "lucide-react";

const Btn = ({ href, icon: Icon, label, cls, testid }) => (
  <a href={href} target="_blank" rel="noreferrer" className={`flex items-center justify-center gap-2 py-3 rounded-xl border text-sm font-bold transition-all duration-300 hover:-translate-y-0.5 hover:brightness-125 ${cls}`} data-testid={testid}>
    <Icon size={17} /> {label}
  </a>
);

const formatPhone = (raw) => {
  const d = (raw || "").replace(/\D/g, "");
  if (d.startsWith("58") && d.length === 12) return `+58 ${d.slice(2, 5)}-${d.slice(5, 8)}-${d.slice(8)}`;
  return raw;
};

export const StoreCard = ({ store, large = false }) => {
  const phones = [store.whatsapp, store.whatsapp2].filter(Boolean);
  return (
    <article
      className={`relative rounded-3xl overflow-hidden bg-[#060812] border border-cyan-500/20 shadow-[0_0_0_1px_rgba(34,211,238,0.08),0_30px_80px_rgba(0,0,0,0.6)] transition-all duration-300 hover:border-cyan-400/40 hover:shadow-[0_0_40px_rgba(34,211,238,0.15),0_30px_80px_rgba(0,0,0,0.6)] ${large ? "" : "hover:-translate-y-1"}`}
      data-testid={`store-card-${store.id}`}
    >
      <div className="absolute inset-0 pointer-events-none bg-[radial-gradient(circle_at_top_right,rgba(168,85,247,0.12),transparent_45%),radial-gradient(circle_at_bottom_left,rgba(34,211,238,0.08),transparent_45%)]" />

      <div className={`relative overflow-hidden ${large ? "h-72 md:h-80" : "h-52"}`}>
        <iframe
          title={`Mapa ${store.nombre}`}
          src={store.embed_url}
          className="absolute inset-0 w-full h-full border-0 pointer-events-none"
          style={{ filter: "invert(92%) hue-rotate(185deg) saturate(0.6) brightness(0.85) contrast(1.05)", transform: "scale(1.02)" }}
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          tabIndex={-1}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#060812] via-[#060812]/30 to-transparent" />
        <a href={store.maps_url} target="_blank" rel="noreferrer" className="absolute top-4 left-4 flex items-center gap-2 px-4 py-2 rounded-xl bg-[#0b1020]/85 backdrop-blur border border-cyan-400/40 text-cyan-300 font-bold text-sm hover:bg-cyan-500/20 transition" data-testid="store-maps-overlay-link">
          Maps <ExternalLink size={15} />
        </a>
        <a href={store.maps_url} target="_blank" rel="noreferrer" className="absolute inset-0" aria-label={`Abrir ${store.nombre} en Google Maps`} />
      </div>

      <div className={`relative ${large ? "px-7 pb-7" : "px-5 pb-5"}`}>
        <div className="flex items-end gap-4 -mt-14 mb-5">
          <div className={`${large ? "w-32 h-32" : "w-24 h-24"} rounded-2xl bg-[#0b0f1c] border-2 border-cyan-400/40 shadow-[0_0_30px_rgba(34,211,238,0.35)] p-2 flex items-center justify-center flex-shrink-0`}>
            {store.logo_url ? <img src={store.logo_url} alt={store.nombre} className="w-full h-full object-contain" /> : <Store size={40} className="text-yellow-400" />}
          </div>
          <div className="flex flex-col gap-2 pb-2">
            <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-black tracking-wider border w-fit ${store.estatus === "Oficial" ? "bg-purple-600/30 text-purple-200 border-purple-400/50" : "bg-cyan-600/20 text-cyan-200 border-cyan-400/40"}`} data-testid={`store-status-${store.estatus.toLowerCase()}`}>
              <BadgeCheck size={13} /> {store.estatus === "Oficial" ? "TIENDA" : "TIENDA ALIADA"}
            </span>
            {store.ciudad && <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-black tracking-wider border border-gray-500/40 bg-black/50 text-gray-300 w-fit"><MapPin size={12} className="text-pink-400" /> {store.ciudad.toUpperCase()}</span>}
          </div>
        </div>

        <h3 className={`font-black text-white leading-tight mb-4 ${large ? "text-3xl md:text-4xl" : "text-2xl"}`} data-testid="store-name">{store.nombre}</h3>
        <div className="space-y-2.5 mb-6">
          <p className="flex items-start gap-3 text-gray-300 text-sm md:text-base"><MapPin size={18} className="text-pink-400 flex-shrink-0 mt-0.5" /> <span>{store.direccion_texto}</span></p>
          {phones.map((p) => <p key={p} className="flex items-center gap-3 text-gray-300 text-sm md:text-base"><Phone size={18} className="text-pink-400 flex-shrink-0" /> {formatPhone(p)}</p>)}
        </div>
        {store.descripcion && large && <p className="text-gray-400 text-sm mb-6">{store.descripcion}</p>}

        <div className="grid grid-cols-2 gap-3">
          <Btn href={store.maps_url} icon={Map} label="Maps" cls="bg-indigo-950/70 border-indigo-400/40 text-white" testid="store-maps-link" />
          {store.whatsapp_url && <Btn href={store.whatsapp_url} icon={MessageCircle} label="WhatsApp" cls="bg-emerald-950/70 border-emerald-400/40 text-emerald-300" testid="store-whatsapp-link" />}
          {store.whatsapp2_url && <Btn href={store.whatsapp2_url} icon={MessageCircle} label="WhatsApp 2" cls="bg-emerald-950/70 border-emerald-400/40 text-emerald-300" testid="store-whatsapp2-link" />}
          {store.instagram_url && <Btn href={store.instagram_url} icon={Instagram} label="Instagram" cls="bg-pink-950/70 border-pink-400/40 text-pink-300" testid="store-instagram-link" />}
          {store.facebook && <Btn href={store.facebook} icon={Facebook} label="Facebook" cls="bg-blue-950/70 border-blue-400/40 text-blue-300" testid="store-facebook-link" />}
          {store.website && <Btn href={store.website} icon={Globe} label="Website" cls="bg-yellow-950/60 border-yellow-400/40 text-yellow-300" testid="store-website-link" />}
        </div>
      </div>
    </article>
  );
};
